import generalSettings from '@apis/generalSettings'
import Button from '@components/button'
import Column from '@components/column'
import Input from '@components/fields/input'
import Row from '@components/row'
import Wrapper from '@components/wrapper'
import { RootState } from '@store/index'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useIsMobile from '@hooks/useIsMobile'
import styled from '@emotion/styled'
import { toast } from 'react-toastify'

const AppSettings = () => {
  const { data: appSettingsData = [] } = useSelector(
    (state: RootState) => state.appSettings,
  )

  const appSettings = appSettingsData || []
  const isMobile = useIsMobile()

  const [loadingAndroid, setLoadingAndroid] = useState(false)
  const [loadingIOS, setLoadingIOS] = useState(false)

  const fetchSettings = async () => {
    try {
      await generalSettings.getByGroup({
        id: 'app-settings',
        state: 'appSettings',
      })
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const androidSetting =
    appSettings.find((item) => item.key === 'android') || {}
  const iosSetting = appSettings.find((item) => item.key === 'ios') || {}

  const handleandroidSubmit = async (values) => {
    setLoadingAndroid(true)
    try {
      const payload = {
        key: 'android',
        group: 'app-settings',
        title: values.playStoreApp,
        value: values.playStoreAppLink,
      }

      if (androidSetting.id) {
        await generalSettings.update({
          id: androidSetting.id,
          state: 'appSettings',
          data: JSON.stringify(payload),
          config: {
            successMsg: 'Android settings updated successfully!',
            store: {
              key: 'appSettings',
              action: 'update',
            },
          },
        })
      } else {
        await generalSettings.create({
          state: 'appSettings',
          data: JSON.stringify(payload),
          config: {
            successMsg: 'Android settings created successfully!',
            store: {
              key: 'appSettings',
              action: 'update',
            },
          },
        })
      }

      // Refetch data to ensure UI is updated
      await fetchSettings()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update Android settings')
    } finally {
      setLoadingAndroid(false)
    }
  }

  const handleiosSubmit = async (values) => {
    setLoadingIOS(true)
    try {
      const payload = {
        key: 'ios',
        group: 'app-settings',
        title: values.appleStoreApp,
        value: values.appleStoreAppLink,
      }

      if (iosSetting.id) {
        await generalSettings.update({
          id: iosSetting.id,
          state: 'appSettings',
          data: JSON.stringify(payload),
          config: {
            successMsg: 'iOS settings updated successfully!',
            store: {
              key: 'appSettings',
              action: 'update',
            },
          },
        })
      } else {
        await generalSettings.create({
          state: 'appSettings',
          data: JSON.stringify(payload),
          config: {
            successMsg: 'iOS settings created successfully!',
            store: {
              key: 'appSettings',
              action: 'update',
            },
          },
        })
      }

      // Refetch data to ensure UI is updated
      await fetchSettings()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update iOS settings')
    } finally {
      setLoadingIOS(false)
    }
  }

  return (
    <Wrapper>
      {/* Play Store Settings Form */}
      <div className="form-section">
        <Formik
          enableReinitialize
          initialValues={{
            playStoreApp: androidSetting?.title || '',
            playStoreAppLink: androidSetting?.value || '',
          }}
          onSubmit={handleandroidSubmit}
        >
          <Form>
            <Column gap={isMobile ? 16 : 24}>
              <Row>
                <div
                  className="input-container"
                  style={{ flex: isMobile ? 1 : 2, width: isMobile && '100%' }}
                >
                  <Input
                    label="Play Store App Version"
                    name="playStoreApp"
                    placeholder="Type here"
                  />
                </div>
                <div
                  className="input-container"
                  style={{ flex: isMobile ? 1 : 4, width: isMobile && '100%' }}
                >
                  <Input
                    label="Play Store Link"
                    name="playStoreAppLink"
                    placeholder="Type here"
                  />
                </div>
              </Row>
              <div className="button-container">
                <Button
                  type="submit"
                  title={loadingAndroid ? 'Loading...' : 'Save'}
                  justify="end"
                />
              </div>
            </Column>
          </Form>
        </Formik>
      </div>

      {/* Apple Store Settings Form */}
      <div className="form-section">
        <Formik
          enableReinitialize
          initialValues={{
            appleStoreApp: iosSetting?.title || '',
            appleStoreAppLink: iosSetting?.value || '',
          }}
          onSubmit={handleiosSubmit}
        >
          <Form>
            <Column gap={isMobile ? 16 : 24}>
              <Row>
                <div
                  className="input-container"
                  style={{ flex: isMobile ? 1 : 2, width: isMobile && '100%' }}
                >
                  <Input
                    label="Apple Store App Version"
                    name="appleStoreApp"
                    placeholder="Type here"
                  />
                </div>
                <div
                  className="input-container"
                  style={{ flex: isMobile ? 1 : 4, width: isMobile && '100%' }}
                >
                  <Input
                    label="Apple Store Link"
                    name="appleStoreAppLink"
                    placeholder="Type here"
                  />
                </div>
              </Row>
              <div className="button-container">
                <Button
                  type="submit"
                  title={loadingIOS ? 'Loading...' : 'Save'}
                  justify="end"
                />
              </div>
            </Column>
          </Form>
        </Formik>
      </div>
    </Wrapper>
  )
}

export default AppSettings
